import React, {
  useReducer,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import {
  usePodTemplateWebSocket,
  type PodInstancePayload,
  type CtaPayload,
} from "@/hooks/usePodTemplateWebSocket";
import { useNavigate } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartKanban,
  faList,
  faAt,
  faHashtag,
  faArrowUp,
  faArrowDown,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import {
  faExclamationTriangle,
  faCheckCircle,
  faCircle,
  faClock,
  faEye,
  faSpinner,
} from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
// Table imports removed - now using Kanban board
import {
  KanbanBoard,
  type KanbanColumnData,
  type KanbanCardData,
} from "@/components/kanban";
// StatusPill removed - now using Kanban cards
import { Button } from "@/components/ui/button/Button";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "@/components/ui/dialog/Dialog";
import * as AgenticAPI from "@/services/api/agentic";
import type {
  PodInstance,
  PodTemplate,
  CallToAction as CallToActionType,
} from "@/services/api/agentic/types";
import Drawer from "@/components/ui/drawer";
import { CallToAction } from "./CallToAction";
import LoadingPlaceholder from "../ui/loading-placeholder/LoadingPlaceholder";
import {
  SearchBar,
  type SearchPill,
  type FilterConfig,
} from "@/components/ui/search";
import ButtonGroup, {
  type ButtonGroupOption,
} from "@/components/ui/button-group";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from "@/components/ui/table/Table";
import { LoadingIndicator } from "@/components/indicators/LoadingIndicator";
import { useOsActionsOptional } from "@/components/ui/os-layout";
import {
  asRenderableText,
  podCurrentStatusToDisplayString,
  podCurrentStatusToMachineString,
} from "@/utils/podStatus";

// Define the PodInstance type for the component (can be enhanced if needed)
// type UnifiedPodInstance = PodInstance; // Using the imported type directly
// Let's define a more specific type for what the table will use, mapping from PodInstance
interface DisplayablePodInstance {
  id: string;
  name: string;
  status: string;
  template_id: string;
  created_at: string;
  updated_at: string;
  owner_id?: string;
  client_id?: string;
  current_stage?: string;
  call_to_action?: string;
  ani_update?: string;
  progress_percentage?: number;
}

// Define the state type
type State = {
  podInstances: DisplayablePodInstance[]; // Changed to DisplayablePodInstance
  loading: boolean;
  error: string | null;
  refreshCounter: number;
  deletingInstances: Set<string>;
};

// Define the action types
type Action =
  | { type: "FETCH_START" }
  | { type: "FETCH_SUCCESS"; payload: DisplayablePodInstance[] } // Changed to DisplayablePodInstance
  | { type: "FETCH_ERROR"; payload: string }
  | { type: "REFRESH" }
  | { type: "DELETE_START"; payload: string }
  | { type: "DELETE_SUCCESS"; payload: string }
  | { type: "DELETE_ERROR"; payload: { id: string; error: string } }
  | { type: "WS_INSTANCE_CREATED"; payload: DisplayablePodInstance }
  | { type: "WS_INSTANCE_UPDATED"; payload: DisplayablePodInstance }
  | { type: "WS_INSTANCE_DELETED"; payload: string };

// Define the reducer function
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "FETCH_START":
      return { ...state, loading: true, error: null };
    case "FETCH_SUCCESS":
      return { ...state, loading: false, podInstances: action.payload };
    case "FETCH_ERROR":
      return { ...state, loading: false, error: action.payload };
    case "REFRESH":
      return {
        ...state,
        loading: true, // Ensure loading is true on refresh
        refreshCounter: state.refreshCounter + 1,
      };
    case "DELETE_START":
      return {
        ...state,
        deletingInstances: new Set([
          ...state.deletingInstances,
          action.payload,
        ]),
      };
    case "DELETE_SUCCESS":
      return {
        ...state,
        deletingInstances: new Set(
          [...state.deletingInstances].filter((id) => id !== action.payload)
        ),
        podInstances: state.podInstances.filter(
          (instance) => instance.id !== action.payload
        ),
      };
    case "DELETE_ERROR":
      return {
        ...state,
        deletingInstances: new Set(
          [...state.deletingInstances].filter((id) => id !== action.payload.id)
        ),
        error: action.payload.error,
      };
    case "WS_INSTANCE_CREATED":
      // Add new instance to the beginning of the list (most recent first)
      // Check if it already exists to avoid duplicates
      if (state.podInstances.some((i) => i.id === action.payload.id)) {
        return state;
      }
      return {
        ...state,
        podInstances: [action.payload, ...state.podInstances],
      };
    case "WS_INSTANCE_UPDATED":
      return {
        ...state,
        podInstances: state.podInstances.map((instance) =>
          instance.id === action.payload.id
            ? { ...instance, ...action.payload }
            : instance
        ),
      };
    case "WS_INSTANCE_DELETED":
      return {
        ...state,
        podInstances: state.podInstances.filter(
          (instance) => instance.id !== action.payload
        ),
      };
    default:
      return state;
  }
}

interface PodInstanceListProps {
  title?: string;
  templateId?: string;
  podTemplate?: PodTemplate;
  /** Default view mode - "kanban" or "list". Defaults to "kanban". */
  defaultView?: ViewMode;
  /** Hide the search bar and view toggle header. Defaults to false. */
  hideHeader?: boolean;
}

type ViewMode = "list" | "kanban";

// Helper function to format status strings
const formatStatus = (status: string | undefined | null): string => {
  if (!status) {
    return "No status";
  }
  return status
    .toLowerCase()
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Define the ref type for the component (if manual refresh is needed from parent)
export interface PodInstanceListRefType {
  refreshPodInstances: () => void;
}

export const PodInstanceList = React.forwardRef<
  PodInstanceListRefType,
  PodInstanceListProps
>(function PodInstanceList(
  { title = "Pod Instances", templateId, podTemplate, defaultView = "kanban", hideHeader = false }: PodInstanceListProps,
  ref
) {
  const navigate = useNavigate();
  const osActions = useOsActionsOptional();

  /**
   * Navigate to a pod instance - opens a new window in OS mode,
   * or navigates via URL in classic mode.
   */
  const navigateToInstance = useCallback(
    (instance: DisplayablePodInstance) => {
      if (osActions) {
        osActions.openWindow({
          appId: "pod-instance",
          title: instance.name,
          params: {
            instanceId: instance.id,
            templateId: instance.template_id,
          },
        });
      } else {
        navigate(`/pod/${instance.template_id}/${instance.id}`);
      }
    },
    [osActions, navigate]
  );

  // Initial state
  const initialState: State = {
    podInstances: [],
    loading: true,
    error: null,
    refreshCounter: 0,
    deletingInstances: new Set(),
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    instance: DisplayablePodInstance | null;
  }>({ open: false, instance: null });
  const [viewMode, setViewMode] = useState<ViewMode>(defaultView);
  const [searchPills, setSearchPills] = useState<SearchPill[]>([]);
  const [sortBy, setSortBy] = useState<keyof DisplayablePodInstance | null>(
    null
  );
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [isCtaDrawerOpen, setIsCtaDrawerOpen] = useState(false);
  const [selectedInstance, setSelectedInstance] =
    useState<DisplayablePodInstance | null>(null);
  const [allCallToActions, setAllCallToActions] = useState<CallToActionType[]>(
    []
  );
  const [instanceCallToActions, setInstanceCallToActions] = useState<
    CallToActionType[]
  >([]);
  const [loadingCallToActions, setLoadingCallToActions] = useState(true);

  const isMounted = useRef(true);
  const isLoadingRef = useRef(false);

  // WebSocket integration for real-time updates
  const { isConnected } = usePodTemplateWebSocket({
    templateId,
    onInstanceCreated: useCallback(
      (instance: PodInstancePayload) => {
        const displayable: DisplayablePodInstance = {
          id: instance.id,
          name: instance.name,
          status: instance.state?.current_status || "Unknown",
          template_id: templateId || "",
          created_at: instance.created_at || new Date().toISOString(),
          updated_at: instance.updated_at || new Date().toISOString(),
          current_stage: instance.state?.current_stage,
        };
        dispatch({ type: "WS_INSTANCE_CREATED", payload: displayable });
      },
      [templateId]
    ),
    onInstanceUpdated: useCallback(
      (instance: PodInstancePayload) => {
        const displayable: DisplayablePodInstance = {
          id: instance.id,
          name: instance.name,
          status: instance.state?.current_status || "Unknown",
          template_id: templateId || "",
          created_at: "",
          updated_at: instance.updated_at || new Date().toISOString(),
          current_stage: instance.state?.current_stage,
        };
        dispatch({ type: "WS_INSTANCE_UPDATED", payload: displayable });
      },
      [templateId]
    ),
    onInstanceDeleted: useCallback((instanceId: string) => {
      dispatch({ type: "WS_INSTANCE_DELETED", payload: instanceId });
    }, []),
    onCtaCreated: useCallback((cta: CtaPayload) => {
      setAllCallToActions((prev) => [
        {
          id: cta.id,
          _id: cta.id,
          pod_instance_id: cta.pod_instance_id,
          task_id: "",
          category: cta.category,
          remedy_type: "button_action",
          message: cta.message,
          priority: cta.priority,
          metadata: {},
          status: cta.status,
        },
        ...prev,
      ]);
    }, []),
    onCtaCompleted: useCallback((ctaId: string) => {
      setAllCallToActions((prev) =>
        prev.filter((c) => c.id !== ctaId && c._id !== ctaId)
      );
    }, []),
    onCtaCancelled: useCallback((ctaId: string) => {
      setAllCallToActions((prev) =>
        prev.filter((c) => c.id !== ctaId && c._id !== ctaId)
      );
    }, []),
    enabled: !!templateId,
  });

  // Function to load pod instances
  async function loadPodInstances() {
    if (isLoadingRef.current || !isMounted.current) return;

    isLoadingRef.current = true;
    dispatch({ type: "FETCH_START" });

    try {
      // Pass templateId if available
      const params = templateId
        ? { template_id: templateId, sort_by: "updated_at", sort_order: "desc", limit: 500 }
        : { sort_by: "updated_at", sort_order: "desc", limit: 500 };
      const response = await AgenticAPI.getPodInstances(params);

      if (isMounted.current) {
        // getPodInstances returns PaginatedResponse<PodInstance> ({ results, count }).
        const rawInstances: PodInstance[] = Array.isArray(response)
          ? response
          : (response.results ?? response.data ?? []);

        const displayableInstances: DisplayablePodInstance[] = rawInstances.map(
          (instance) => ({
            id: instance.id || (instance as any)._id,
            name: instance.name,
            status:
              podCurrentStatusToMachineString(
                instance.state?.current_status
              ) || "Unknown",
            template_id: instance.template_id,
            created_at: instance.created_at,
            updated_at: instance.updated_at,
            owner_id: instance.owner_id,
            client_id: instance.client_id,
            current_stage:
              podCurrentStatusToDisplayString(
                instance.state?.current_stage
              ) ||
              podCurrentStatusToMachineString(
                instance.state?.current_stage
              ) ||
              undefined,
            call_to_action: instance.state?.call_to_action,
            ani_update: instance.ani_update,
            progress_percentage:
              (instance as any).progress_percentage ||
              (instance as any).state?.progress_percentage,
          })
        );

        dispatch({ type: "FETCH_SUCCESS", payload: displayableInstances });
      }
    } catch (err) {
      console.error("Error loading pod instances:", err);
      if (isMounted.current) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load pod instances";
        dispatch({ type: "FETCH_ERROR", payload: errorMessage });
      }
    } finally {
      isLoadingRef.current = false;
    }
  }

  // Function to manually trigger a refresh
  function refreshPodInstances() {
    if (!isLoadingRef.current && isMounted.current) {
      dispatch({ type: "REFRESH" });
    }
  }

  // Handler to open CTA drawer
  const handleOpenCtaDrawer = (
    instance: DisplayablePodInstance,
    e: React.MouseEvent
  ) => {
    e.stopPropagation(); // Prevent row click navigation
    setSelectedInstance(instance);
    setIsCtaDrawerOpen(true);

    // Filter the already-fetched CTAs for this instance
    const instanceCtas = allCallToActions.filter(
      (cta: any) => cta.pod_instance_id === instance.id
    );
    setInstanceCallToActions(instanceCtas);
  };

  // Handler to close CTA drawer
  const handleCloseCtaDrawer = () => {
    setIsCtaDrawerOpen(false);
    setSelectedInstance(null);
    setInstanceCallToActions([]);
  };

  // Expose the refresh function via ref
  React.useImperativeHandle(
    ref,
    () => ({
      refreshPodInstances,
    }),
    []
  );

  // Cleanup on unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Effect to handle initial load and refreshes
  useEffect(() => {
    loadPodInstances();
  }, [state.refreshCounter, templateId]);

  // Fetch call-to-actions for the template
  useEffect(() => {
    const fetchCallToActions = async () => {
      if (!templateId) {
        setLoadingCallToActions(false);
        return;
      }

      setLoadingCallToActions(true);
      try {
        const ctas = await AgenticAPI.getCallToActionsByTemplate(templateId, { limit: 500 });
        // Filter to only show active CTAs (live and processing), exclude completed and cancelled
        const activeCtas = ctas.filter(
          (cta: any) => cta.status === "live" || cta.status === "processing"
        );
        setAllCallToActions(activeCtas);
      } catch (error) {
        console.error("Error fetching call-to-actions:", error);
        setAllCallToActions([]);
      } finally {
        setLoadingCallToActions(false);
      }
    };

    fetchCallToActions();
  }, [templateId]);

  // Delete handlers
  const handleDeleteClick = (
    instance: DisplayablePodInstance,
    event: React.MouseEvent
  ) => {
    event.stopPropagation(); // Prevent row click navigation
    setDeleteDialog({ open: true, instance });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.instance) return;

    const instanceId = deleteDialog.instance.id;
    dispatch({ type: "DELETE_START", payload: instanceId });

    try {
      await AgenticAPI.deletePodInstance(instanceId);
      dispatch({ type: "DELETE_SUCCESS", payload: instanceId });
      setDeleteDialog({ open: false, instance: null });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to delete pod instance";
      dispatch({
        type: "DELETE_ERROR",
        payload: { id: instanceId, error: errorMessage },
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, instance: null });
  };

  const isDeleting = (instanceId: string) =>
    state.deletingInstances.has(instanceId);

  // Get unique statuses and stages for filters
  const uniqueStatuses = useMemo(() => {
    const statuses = new Set<string>();
    state.podInstances.forEach((instance) => {
      if (instance.status) {
        statuses.add(instance.status);
      }
    });
    return Array.from(statuses).sort();
  }, [state.podInstances]);

  const uniqueStages = useMemo(() => {
    const stages = new Set<string>();
    state.podInstances.forEach((instance) => {
      if (instance.current_stage) {
        stages.add(instance.current_stage);
      }
    });
    return Array.from(stages).sort();
  }, [state.podInstances]);

  // Filter configuration
  const filterConfigs: FilterConfig[] = useMemo(
    () => [
      {
        type: "status",
        symbol: "@",
        label: "Status",
        options: uniqueStatuses,
        icon: <FontAwesomeIcon icon={faAt} />,
      },
      {
        type: "stage",
        symbol: "#",
        label: "Stage",
        options: uniqueStages,
        icon: <FontAwesomeIcon icon={faHashtag} />,
      },
    ],
    [uniqueStatuses, uniqueStages]
  );

  // Helper function to get progress bar color based on status and priority
  const getProgressBarColor = (
    status: string,
    hasCallToAction: boolean,
    ctaPriority?: string
  ): string => {
    const statusLower = status.toLowerCase();

    if (statusLower.includes("completed") || statusLower === "complete") {
      return "bg-green-500";
    } else if (statusLower.includes("review")) {
      // Use CTA priority color if available, otherwise use default amber
      if (ctaPriority) {
        const priorityLower = ctaPriority.toLowerCase();
        if (priorityLower === "high") {
          return "bg-red-400";
        } else if (priorityLower === "medium") {
          return "bg-yellow-400";
        } else if (priorityLower === "low") {
          return "bg-green-500";
        }
      }
      return "bg-amber-400";
    } else if (statusLower.includes("blocked") || hasCallToAction) {
      return "bg-red-400";
    } else if (
      statusLower.includes("scheduled") ||
      statusLower.includes("schedule")
    ) {
      return "bg-gray-500";
    } else {
      // Default for in-progress or unknown
      return "bg-indigo-500";
    }
  };

  // Filter and sort instances
  const filteredAndSortedInstances = useMemo(() => {
    let filtered = state.podInstances;

    // Apply filters
    if (searchPills.length > 0) {
      // Group pills by type for smart filtering
      const pillsByType = searchPills.reduce(
        (acc, pill) => {
          if (!acc[pill.type]) {
            acc[pill.type] = [];
          }
          acc[pill.type].push(pill);
          return acc;
        },
        {} as Record<string, SearchPill[]>
      );

      filtered = state.podInstances.filter((instance) => {
        // For each filter type, at least one pill of that type must match (OR within type)
        // All filter types must match (AND across types)
        return Object.entries(pillsByType).every(([type, pills]) => {
          // OR logic: at least one pill of this type must match
          return pills.some((pill) => {
            const searchValue = pill.value.toLowerCase();

            if (type === "search") {
              // Search in name, status, stage, and updates
              return (
                instance.name.toLowerCase().includes(searchValue) ||
                instance.status?.toLowerCase().includes(searchValue) ||
                instance.current_stage?.toLowerCase().includes(searchValue) ||
                instance.ani_update?.toLowerCase().includes(searchValue) ||
                instance.call_to_action?.toLowerCase().includes(searchValue)
              );
            } else if (type === "status") {
              return instance.status?.toLowerCase() === searchValue;
            } else if (type === "stage") {
              return instance.current_stage?.toLowerCase() === searchValue;
            }

            return false;
          });
        });
      });
    }

    // Apply sorting
    if (sortBy) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortBy];
        let bValue = b[sortBy];

        // Handle null/undefined values
        if (aValue === null || aValue === undefined) aValue = "";
        if (bValue === null || bValue === undefined) bValue = "";

        // Handle date sorting
        if (sortBy === "updated_at" || sortBy === "created_at") {
          const aTime = new Date(aValue as string).getTime();
          const bTime = new Date(bValue as string).getTime();
          return sortDirection === "asc" ? aTime - bTime : bTime - aTime;
        }

        // Handle number sorting (progress_percentage)
        if (sortBy === "progress_percentage") {
          const aNum = (aValue as number) || 0;
          const bNum = (bValue as number) || 0;
          return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
        }

        // Handle string sorting
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();

        if (aStr < bStr) return sortDirection === "asc" ? -1 : 1;
        if (aStr > bStr) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [state.podInstances, searchPills, sortBy, sortDirection]);

  // Handle column header click for sorting
  const handleSort = (column: keyof DisplayablePodInstance) => {
    if (sortBy === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new column and default to ascending
      setSortBy(column);
      setSortDirection("asc");
    }
  };

  // Render sort icon
  const renderSortIcon = (column: keyof DisplayablePodInstance) => {
    if (sortBy !== column) return null;
    return (
      <FontAwesomeIcon
        icon={sortDirection === "asc" ? faArrowUp : faArrowDown}
        className="ml-1 text-xs"
      />
    );
  };

  // Helper function to generate colors for columns
  const generateColumnColor = (index: number): string => {
    const colors = [
      "#A0AEC0", // Light Gray
      "#EF4444", // Red
      "#63B3ED", // Blue
      "#68D391", // Green
      "#F59E0B", // Amber
      "#8B5CF6", // Purple
      "#EC4899", // Pink
      "#10B981", // Emerald
    ];
    return colors[index % colors.length];
  };

  // Generate dynamic Kanban columns from pod template workflow steps
  const generateKanbanColumns = (): KanbanColumnData[] => {
    if (!podTemplate?.workflow_config?.steps) {
      // Fallback to default columns if no template
      return [
        {
          id: "unknown",
          title: "Unknown Stage",
          color: "#A0AEC0",
          cards: [],
        },
      ];
    }

    // Sort steps by order and create columns
    const sortedSteps = [...podTemplate.workflow_config.steps].sort(
      (a, b) => a.order - b.order
    );

    return sortedSteps.map((step, index) => ({
      id: step.label,
      title: step.label,
      color: generateColumnColor(index),
      cards: [],
    }));
  };

  // Helper to render status badge (matches TaskChecklist style)
  const renderStatusBadge = (
    status: unknown,
    hasCallToAction: boolean,
    showArrow: boolean = false,
    ctaPriority?: string
  ) => {
    const normalized = podCurrentStatusToMachineString(status) || "Unknown";
    let bgColor = "bg-slate-900/20";
    let textColor = "text-slate-600 dark:text-slate-400";
    let icon = faCircle;

    const statusLower = normalized.toLowerCase();
    const isInProgress = statusLower.includes("progress");
    const isReview = statusLower.includes("review");

    // Check status based on lowercase comparison for more flexibility
    if (statusLower.includes("completed") || statusLower === "complete") {
      bgColor = "bg-green-500/20";
      textColor = "text-green-600 dark:text-green-400";
      icon = faCheckCircle;
    } else if (isInProgress) {
      bgColor = "bg-slate-900/20";
      textColor = "text-slate-600 dark:text-slate-400";
      icon = faSpinner;
    } else if (isReview) {
      // Use CTA priority color if available, otherwise use default amber
      if (ctaPriority) {
        const priorityLower = ctaPriority.toLowerCase();
        if (priorityLower === "high") {
          bgColor = "bg-red-500/20";
          textColor = "text-red-600 dark:text-red-400";
          icon = faExclamationTriangle;
        } else if (priorityLower === "medium") {
          bgColor = "bg-yellow-500/20";
          textColor = "text-yellow-600 dark:text-yellow-400";
          icon = faEye;
        } else if (priorityLower === "low") {
          bgColor = "bg-green-500/20";
          textColor = "text-green-600 dark:text-green-400";
          icon = faEye;
        } else {
          bgColor = "bg-amber-500/20";
          textColor = "text-amber-600 dark:text-amber-400";
          icon = faEye;
        }
      } else {
        bgColor = "bg-amber-500/20";
        textColor = "text-amber-600 dark:text-amber-400";
        icon = faEye;
      }
    } else if (
      statusLower.includes("scheduled") ||
      statusLower.includes("schedule")
    ) {
      bgColor = "bg-gray-500/20";
      textColor = "text-gray-600 dark:text-gray-400";
      icon = faClock;
    } else if (statusLower.includes("blocked") || hasCallToAction) {
      bgColor = "bg-red-500/20";
      textColor = "text-red-600 dark:text-red-400";
      icon = hasCallToAction ? faExclamationTriangle : faCircle;
    }

    return (
      <span
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-medium ${bgColor} ${textColor}`}
      >
        {isInProgress ? (
          <LoadingIndicator
            dotSize={2}
            dotCount={3}
            showText={false}
            dotSpacing={1}
          />
        ) : (
          <FontAwesomeIcon icon={icon} />
        )}
        {formatStatus(normalized)}
        {showArrow && <span>&rarr;</span>}
      </span>
    );
  };

  // Helper function to map a Pod Instance to KanbanCardData
  const mapInstanceToKanbanCard = (
    instance: DisplayablePodInstance
  ): KanbanCardData => {
    const columnId = instance.current_stage || "Unknown Stage";

    return {
      id: instance.id,
      title: instance.name,
      content: instance.ani_update || "", // Just the ani_update text
      columnId: columnId,
      assigneeName: undefined, // Pod instances don't have assignees like tasks
      status: {
        current_status: instance.status,
        details: instance.call_to_action || instance.ani_update || "",
      },
      notes: instance.ani_update || "",
      progress_percentage: undefined,
      current_state: instance.current_stage,
      onClick: () => navigateToInstance(instance),
    };
  };

  if (state.loading) {
    return <div className="p-4">Loading cases...</div>;
  }

  if (state.error) {
    return <div className="p-4 text-red-500">Error: {state.error}</div>;
  }

  // Generate the Kanban columns with pod instances
  const instanceCards: KanbanCardData[] = filteredAndSortedInstances.map(
    mapInstanceToKanbanCard
  );
  const kanbanColumns = generateKanbanColumns().map((column) => ({
    ...column,
    cards: instanceCards.filter((card) => card.columnId === column.id),
  }));

  const handleCardMove = (
    cardId: string,
    sourceColumnId: string,
    destinationColumnId: string,
    newIndex: number
  ) => {
    // Handle moving pod instances between stages
    // This would update the pod instance's current_stage
    // TODO: Implement API call to update pod instance stage
  };

  const viewToggleOptions: ButtonGroupOption<ViewMode>[] = [
    {
      value: "list",
      label: (
        <span className="flex items-center gap-2">
          <FontAwesomeIcon icon={faList} />
        </span>
      ),
    },
    {
      value: "kanban",
      label: (
        <span className="flex items-center gap-2">
          <FontAwesomeIcon icon={faChartKanban} />
        </span>
      ),
    },
  ];

  return (
    <>
      <div className="flex flex-col h-full">
        {/* Search and Filter Bar - conditionally shown */}
        {!hideHeader && (
          <div className="p-4 px-0 pb-2 space-y-3">
            {/* View Toggle */}
            <div className="flex justify-end">
              <ButtonGroup
                options={viewToggleOptions}
                value={viewMode}
                onChange={(newMode) => setViewMode(newMode as ViewMode)}
                size="md"
                fullWidth={false}
              />
            </div>
            <SearchBar
              placeholder={`Search by name, @status or #stage...`}
              filters={filterConfigs}
              pills={searchPills}
              onPillsChange={setSearchPills}
            />
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 overflow-auto">
          {state.podInstances.length === 0 ? (
            <div className="p-4 text-gray-500">No pod instances available</div>
          ) : filteredAndSortedInstances.length === 0 ? (
            <div className="p-4 text-gray-500 text-center">
              No cases match your search criteria
            </div>
          ) : viewMode === "list" ? (
            <div className="">
              <Table striped>
                <colgroup>
                  <col className="w-auto" />
                  <col className="w-48" />
                  <col className="w-40" />
                  <col className="w-40" />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableHeader
                      className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
                      onClick={() => handleSort("name")}
                    >
                      <span className="flex items-center">
                        Name
                        {renderSortIcon("name")}
                      </span>
                    </TableHeader>
                    <TableHeader></TableHeader>
                    <TableHeader
                      className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
                      onClick={() => handleSort("status")}
                    >
                      <span className="flex items-center">
                        Status
                        {renderSortIcon("status")}
                      </span>
                    </TableHeader>
                    <TableHeader
                      className="text-right cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
                      onClick={() => handleSort("updated_at")}
                    >
                      <span className="flex items-center justify-end">
                        last updated
                        {renderSortIcon("updated_at")}
                      </span>
                    </TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAndSortedInstances.map((instance) => (
                    <TableRow
                      key={instance.id}
                      onClick={() => navigateToInstance(instance)}
                      className="cursor-pointer"
                    >
                      <TableCell className="text-slate-900 dark:text-slate-100">
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <div className="truncate">{instance.name}</div>
                          {instance.current_stage && (
                            <div className="text-xs text-slate-600 dark:text-slate-500 truncate">
                              {instance.current_stage}
                            </div>
                          )}
                          {instance.call_to_action && (
                            <div className="flex items-center gap-1.5 text-xs text-orange-600 dark:text-orange-400 min-w-0">
                              <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                className="flex-shrink-0"
                              />
                              <span className="truncate">
                                {instance.call_to_action}
                              </span>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {(() => {
                          const hasCallToActions = allCallToActions.some(
                            (cta: any) => cta.pod_instance_id === instance.id
                          );

                          // Calculate highest priority CTA first (only if CTAs are loaded)
                          let highestPriority = "";
                          if (!loadingCallToActions) {
                            const instanceCtas = allCallToActions.filter(
                              (cta: any) => cta.pod_instance_id === instance.id
                            );
                            highestPriority = instanceCtas.reduce(
                              (highest: string, cta: any) => {
                                const priority = (
                                  cta.priority || ""
                                ).toLowerCase();
                                if (priority === "high") return "high";
                                if (priority === "medium" && highest !== "high")
                                  return "medium";
                                if (
                                  priority === "low" &&
                                  highest !== "high" &&
                                  highest !== "medium"
                                )
                                  return "low";
                                return highest;
                              },
                              ""
                            );
                          }

                          const progress =
                            instance.progress_percentage ?? 0.0;  // Default to 0 if no progress available
                          const progressColor = getProgressBarColor(
                            hasCallToActions ? "Needs Review" : instance.status,
                            hasCallToActions,
                            highestPriority
                          );
                          return (
                            <div className="flex items-center justify-center">
                              <div className="w-24 h-2 bg-slate-900/20 rounded-full overflow-hidden">
                                <div
                                  className={`h-full ${progressColor} transition-all duration-300 rounded-full`}
                                  style={{
                                    width: `${Math.round(progress * 100)}%`,
                                  }}
                                />
                              </div>
                              <span className="text-xs font-mono text-slate-600 dark:text-slate-400 rounded-full w-10 text-right">
                                {Math.round(progress * 100)}%
                              </span>
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const hasCallToActions = allCallToActions.some(
                            (cta: any) => cta.pod_instance_id === instance.id
                          );

                          if (!instance.status) {
                            return (
                              <span className="text-sm text-slate-500">—</span>
                            );
                          }

                          const isReviewStatus = instance.status
                            .toLowerCase()
                            .includes("review");

                          // Get the highest priority CTA for this instance (only if CTAs are loaded)
                          let highestPriority = "";
                          if (!loadingCallToActions) {
                            const instanceCtas = allCallToActions.filter(
                              (cta: any) => cta.pod_instance_id === instance.id
                            );
                            highestPriority = instanceCtas.reduce(
                              (highest: string, cta: any) => {
                                const priority = (
                                  cta.priority || ""
                                ).toLowerCase();
                                if (priority === "high") return "high";
                                if (priority === "medium" && highest !== "high")
                                  return "medium";
                                if (
                                  priority === "low" &&
                                  highest !== "high" &&
                                  highest !== "medium"
                                )
                                  return "low";
                                return highest;
                              },
                              ""
                            );
                          }

                          return (
                            <div
                              onClick={
                                hasCallToActions
                                  ? (e: React.MouseEvent) =>
                                      handleOpenCtaDrawer(instance, e)
                                  : undefined
                              }
                              className={
                                hasCallToActions
                                  ? "cursor-pointer hover:opacity-80 transition-opacity"
                                  : ""
                              }
                            >
                              {renderStatusBadge(
                                hasCallToActions
                                  ? "Needs Review"
                                  : instance.status,
                                !!instance.call_to_action,
                                hasCallToActions,
                                highestPriority
                              )}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
                          {new Date(instance.updated_at).toLocaleDateString()}{" "}
                          {new Date(instance.updated_at).toLocaleTimeString(
                            [],
                            {
                              hour: "numeric",
                              minute: "2-digit",
                              hour12: true,
                            }
                          )}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="h-full pt-4">
              <KanbanBoard
                initialColumns={kanbanColumns}
                onCardMove={handleCardMove}
              />
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel} size="md">
        <DialogTitle>Delete Pod Instance</DialogTitle>
        <DialogBody>
          <div className="mb-4">
            <p className="text-gray-700 dark:text-gray-300 mb-3">
              Are you sure you want to delete{" "}
              <strong>"{deleteDialog.instance?.name}"</strong>?
            </p>
            <div className="bg-red-50 dark:bg-red-400 rounded-lg p-4">
              <div className="flex items-start">
                <div className=" text-red-800 dark:text-red-100">
                  <strong>Warning:</strong> This action will permanently delete
                  the pod instance and all its related data including:
                  <ul className="mt-2 ml-4 space-y-1 list-disc">
                    <li>All activities</li>
                    <li>All chat messages</li>
                    <li>All datasets</li>
                    <li>All documents</li>
                    <li>All interactions</li>
                    <li>All portfolio definitions</li>
                    <li>All system jobs</li>
                    <li>All workflow tasks</li>
                  </ul>
                  <p className="mt-2 font-medium">This cannot be undone.</p>
                </div>
              </div>
            </div>
          </div>
        </DialogBody>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button color="red" onClick={handleDeleteConfirm}>
            Delete Pod Instance
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drawer for displaying call-to-actions */}
      <Drawer
        isOpen={isCtaDrawerOpen}
        onClose={handleCloseCtaDrawer}
        title={selectedInstance?.name || "Actions Required"}
        width="max-w-2xl text-white"
      >
        <div className="px-6 pb-6">
          <div>
            {selectedInstance?.ani_update && (
              <div className="text-slate-900 dark:text-slate-100 mb-8">
                {asRenderableText(selectedInstance.ani_update)}
              </div>
            )}
          </div>

          {instanceCallToActions.length > 0 ? (
            <div className="space-y-3">
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0 space-y-4">
                  <div className="grid grid-cols-1 gap-4 text-sm">
                    {instanceCallToActions.map((action, index) => (
                      <div
                        key={action.id || index}
                        className="p-4 flex flex-col rounded-xl space-y-4 transition-all duration-200 bg-white dark:bg-slate-700 hover:shadow-sm"
                      >
                        <CallToAction action={action} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-slate-500 dark:text-slate-400">
                No actions required at this time.
              </p>
            </div>
          )}
        </div>
      </Drawer>
    </>
  );
});
